/**
 * CountUp — the Copilot instrument readout's digit roll. Extends the landing-page CountUp idea for the
 * precision-instrument voice: a figure climbs to its true value and locks — it never overshoots, settles
 * exactly on the formatted target, and starts dimmed (no crisp `0`) so no in-flight frame reads as a real
 * count. The number is backend-computed; this only animates the approach.
 *
 * Differences from landing/sections/CountUp.tsx (kept separate so the hero stats are untouched):
 *  - no crisp `0` start — the first frame is dimmed, like every in-flight frame
 *  - decimal-formatted target (the stat preserves 1 fractional digit; integers stay grouped)
 *  - reduced-motion SNAP — the flag short-circuits before any rAF runs, so the value appears immediately
 *  - monotonic to the target, never exceeding it, settling exactly on the formatted target at lock
 */
import * as React from 'react';
import { animate, useReducedMotion } from 'framer-motion';
import { tokens } from '../tokens';

export interface CountUpProps {
  /** True, backend-computed target. The roll approaches this and settles exactly on it — never past it. */
  value: number;
  /** Tween duration in seconds. */
  duration?: number;
  /** Fractional digits to preserve (the stat keeps 1; a record count keeps 0). */
  fractionDigits?: number;
  /** Static text rendered after the number (e.g. a unit). */
  suffix?: string;
  className?: string;
}

/**
 * Formats to the true precision: big integers stay exact + grouped; non-integers keep up to
 * `fractionDigits` (so 58.31578… reads 58.3). Always locale-grouped via toLocaleString().
 */
function formatValue(n: number, fractionDigits: number) {
  return n.toLocaleString(undefined, { maximumFractionDigits: fractionDigits });
}

export function CountUp({
  value,
  duration = 0.9,
  fractionDigits = 0,
  suffix = '',
  className,
}: CountUpProps) {
  const reduceMotion = useReducedMotion() ?? false;
  const format = React.useCallback(
    (n: number) => formatValue(n, fractionDigits),
    [fractionDigits],
  );

  // Reduced motion (or an already-settled value) renders the final figure immediately; otherwise the roll
  // starts dimmed at a value just shy of the target so no crisp frame reads as a real count.
  const [display, setDisplay] = React.useState<string>(() => format(value));
  // `settled` flips true at lock; until then the digits stay dimmed (in motion, not a true read).
  const [settled, setSettled] = React.useState<boolean>(() => reduceMotion);

  React.useEffect(() => {
    if (reduceMotion) {
      // Snap: no rAF — the final value appears at once, fully settled.
      setDisplay(format(value));
      setSettled(true);
      return;
    }

    setSettled(false);
    const controls = animate(0, value, {
      duration,
      ease: 'easeOut',
      onUpdate: (latest) => {
        // easeOut never overshoots, but clamp defensively so a frame can never exceed the true value.
        setDisplay(format(Math.min(latest, value)));
      },
      onComplete: () => {
        // Settle exactly on the formatted target, then lock (dim → full ink).
        setDisplay(format(value));
        setSettled(true);
      },
    });
    return () => controls.stop();
  }, [value, duration, reduceMotion, format]);

  return (
    <span
      className={className}
      // The rolling digits are decorative motion; only the final value is exposed to AT (once).
      aria-hidden={!settled}
      style={{
        fontVariantNumeric: 'tabular-nums',
        // In-flight digits are dimmed so no crisp intermediate reads as a real count; lock settles to full ink.
        color: settled ? tokens.text.primary : tokens.text.muted,
        transition: 'color 150ms ease-out',
      }}
    >
      {display}
      {suffix}
    </span>
  );
}

export default CountUp;
